import { useEffect, useState, useCallback, useRef } from 'react';
import { withRetry } from '../core/utils/retry';
import {
  OFFLINE_QUEUE_KEY,
  readOfflineQueue,
  writeOfflineQueue,
  retainFailedQueueActions,
  type FlushResult,
} from '../core/offline/queue';

type FlushListener = (results: FlushResult[]) => void;

import type { IDataAdapter } from '../adapters/data/IDataAdapter';

export function useOfflineQueue({
  adapter,
  online,
}: {
  adapter: Pick<IDataAdapter, 'flush'> | null | undefined;
  online: boolean;
}) {
  const [queueSize, setQueueSize] = useState(() => readOfflineQueue().length);
  const listenersRef = useRef(new Set<FlushListener>());

  const notifyFlush = useCallback((results: FlushResult[]) => {
    listenersRef.current.forEach((listener) => listener(results));
  }, []);

  const subscribeFlush = useCallback((listener: FlushListener) => {
    listenersRef.current.add(listener);
    return () => listenersRef.current.delete(listener);
  }, []);

  const enqueue = useCallback((action: { type: string; payload?: unknown; clientId?: string }) => {
    try {
      const clientId = action.clientId ?? crypto.randomUUID();
      const q = readOfflineQueue();
      q.push({
        clientId,
        type: action.type,
        payload: action.payload,
        enqueuedAt: Date.now(),
      });
      writeOfflineQueue(q);
      setQueueSize(q.length);
      return clientId;
    } catch (e) {
      console.error('[OfflineQueue] Failed to enqueue:', e);
      return null;
    }
  }, []);

  useEffect(() => {
    if (!online || !adapter?.flush) return;

    const pending = readOfflineQueue();
    if (!pending.length) return;

    let cancelled = false;

    const drain = async () => {
      try {
        const results = await withRetry(
          () => adapter.flush(pending) as Promise<FlushResult[] | void>,
          { attempts: 4, baseDelayMs: 600 },
        );
        if (cancelled) return;

        const flushResults: FlushResult[] = Array.isArray(results)
          ? results
          : pending.map((action) => ({
            clientId: action.clientId,
            type: action.type,
            ok: true,
          }));

        const remaining = retainFailedQueueActions(pending, flushResults);
        writeOfflineQueue(remaining);
        setQueueSize(remaining.length);
        notifyFlush(flushResults);
      } catch (e) {
        console.error('[OfflineQueue] Flush failed:', e);
      }
    };

    drain();
    return () => {
      cancelled = true;
    };
  }, [online, adapter, notifyFlush]);

  return {
    enqueue,
    queueSize,
    subscribeFlush,
    readQueue: readOfflineQueue,
  };
}

export { OFFLINE_QUEUE_KEY };
